import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { UserCheck, Save, Loader2, Lock, TrendingUp, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const Field = ({ label, value, onChange, type = 'text', placeholder = '', suffix }) => (
  <div className="space-y-1">
    <Label className="text-xs text-zinc-400">{label}</Label>
    <div className="relative">
      <Input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        placeholder={placeholder}
        className={`bg-zinc-950 border-zinc-800 text-white text-sm h-9 ${suffix ? 'pr-10' : ''}`}
      />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500">{suffix}</span>}
    </div>
  </div>
);

const SelectField = ({ label, value, onChange, options }) => (
  <div className="space-y-1">
    <Label className="text-xs text-zinc-400">{label}</Label>
    <Select value={value || ''} onValueChange={onChange}>
      <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-9 text-sm">
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent>
        {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export const BorrowerProfileCard = () => {
  const [profile, setProfile] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/borrower-profile`, { withCredentials: true });
        setProfile(res.data || {});
      } catch {
        toast.error('Impossible de charger votre profil');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = (key, value) => setProfile(prev => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/borrower-profile`, profile, { withCredentials: true });
      setProfile(res.data || profile);
      toast.success('Profil emprunteur enregistré');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  // Calculs en direct
  const totalRevenusMensuel =
    (profile.salaire_net_mensuel || 0) +
    (profile.revenus_emploi_secondaire || 0) +
    (profile.revenus_locatifs_mensuels || 0) +
    (profile.revenus_pension_alimentaire || 0) +
    (profile.revenus_allocations || 0) +
    (profile.revenus_autres || 0) +
    (profile.primes_annuelles || 0) / 12 +
    (profile.treizieme_mois || 0) / 12 +
    (profile.revenus_dividendes_annuels || 0) / 12;

  const totalCharges =
    (profile.loyer_mensuel || 0) +
    (profile.credits_immobiliers_mensualite || 0) +
    (profile.credits_conso_mensualite || 0) +
    (profile.pensions_alimentaires_versees || 0) +
    (profile.autres_charges || 0);

  const tauxEndettement = totalRevenusMensuel > 0 ? (totalCharges / totalRevenusMensuel) * 100 : 0;
  const reste = totalRevenusMensuel - totalCharges;

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2">
        <CardContent className="py-8 text-center text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-zinc-900 border-zinc-800 lg:col-span-2" data-testid="borrower-profile-card">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <UserCheck className="w-5 h-5" style={{ color: 'var(--accent-color, #10B981)' }} />
              Profil emprunteur
            </CardTitle>
            <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1">
              <Lock className="w-3 h-3" /> Strictement privé — utilisé pour générer votre dossier de financement bancaire
            </p>
          </div>
          <Button onClick={save} disabled={saving} className="text-white" style={{ backgroundColor: 'var(--accent-color, #10B981)' }} data-testid="save-borrower-profile">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* KPIs en direct */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-zinc-950/50 rounded-lg border border-zinc-800">
          <div>
            <p className="text-[10px] uppercase text-zinc-500">Revenus / mois</p>
            <p className="text-lg font-bold text-emerald-300">{Math.round(totalRevenusMensuel).toLocaleString('fr-FR')} €</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-zinc-500">Charges / mois</p>
            <p className="text-lg font-bold text-orange-300">{Math.round(totalCharges).toLocaleString('fr-FR')} €</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-zinc-500">Reste à vivre</p>
            <p className="text-lg font-bold text-white">{Math.round(reste).toLocaleString('fr-FR')} €</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-zinc-500">Taux d'endettement</p>
            <p className={`text-lg font-bold ${tauxEndettement > 35 ? 'text-red-400' : tauxEndettement > 30 ? 'text-orange-400' : 'text-emerald-400'}`}>
              {tauxEndettement.toFixed(1)} %
              {tauxEndettement > 35 && <AlertTriangle className="inline w-4 h-4 ml-1" />}
            </p>
          </div>
        </div>

        <Accordion type="multiple" defaultValue={['identite']} className="space-y-2">
          {/* Identité */}
          <AccordionItem value="identite" className="border-zinc-800">
            <AccordionTrigger className="text-sm text-white hover:no-underline">👤 Identité civile</AccordionTrigger>
            <AccordionContent className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <Field label="Prénom" value={profile.first_name} onChange={(v) => update('first_name', v)} />
              <Field label="Nom" value={profile.last_name} onChange={(v) => update('last_name', v)} />
              <Field label="Date de naissance" type="date" value={profile.birth_date} onChange={(v) => update('birth_date', v)} />
              <Field label="Lieu de naissance" value={profile.birth_place} onChange={(v) => update('birth_place', v)} />
              <Field label="Nationalité" value={profile.nationality} onChange={(v) => update('nationality', v)} placeholder="Française" />
              <SelectField label="Situation familiale" value={profile.marital_status} onChange={(v) => update('marital_status', v)}
                options={[
                  { value: 'celibataire', label: 'Célibataire' },
                  { value: 'marie', label: 'Marié(e)' },
                  { value: 'pacse', label: 'Pacsé(e)' },
                  { value: 'divorce', label: 'Divorcé(e)' },
                  { value: 'veuf', label: 'Veuf / Veuve' },
                ]} />
              <Field label="Régime matrimonial" value={profile.regime_matrimonial} onChange={(v) => update('regime_matrimonial', v)} placeholder="Communauté réduite aux acquêts" />
              <Field label="Nombre d'enfants" type="number" value={profile.nb_enfants} onChange={(v) => update('nb_enfants', v)} />
              <Field label="Personnes à charge" type="number" value={profile.nb_personnes_a_charge} onChange={(v) => update('nb_personnes_a_charge', v)} />
              <Field label="Téléphone personnel" value={profile.telephone_perso} onChange={(v) => update('telephone_perso', v)} placeholder="06 12 34 56 78" />
              <div className="md:col-span-2">
                <Field label="Adresse personnelle" value={profile.adresse_perso} onChange={(v) => update('adresse_perso', v)} />
              </div>
              <Field label="Code postal" value={profile.code_postal_perso} onChange={(v) => update('code_postal_perso', v)} />
              <Field label="Ville" value={profile.ville_perso} onChange={(v) => update('ville_perso', v)} />
            </AccordionContent>
          </AccordionItem>

          {/* Situation pro */}
          <AccordionItem value="pro" className="border-zinc-800">
            <AccordionTrigger className="text-sm text-white hover:no-underline">💼 Situation professionnelle</AccordionTrigger>
            <AccordionContent className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <Field label="Profession" value={profile.profession} onChange={(v) => update('profession', v)} placeholder="Cadre commercial" />
              <SelectField label="Statut" value={profile.statut_pro} onChange={(v) => update('statut_pro', v)}
                options={[
                  { value: 'cadre', label: 'Cadre' },
                  { value: 'non_cadre', label: 'Non-cadre' },
                  { value: 'dirigeant', label: 'Dirigeant' },
                  { value: 'profession_liberale', label: 'Profession libérale' },
                  { value: 'fonctionnaire', label: 'Fonctionnaire' },
                  { value: 'artisan', label: 'Artisan / Commerçant' },
                  { value: 'autre', label: 'Autre' },
                ]} />
              <SelectField label="Type de contrat" value={profile.type_contrat} onChange={(v) => update('type_contrat', v)}
                options={[
                  { value: 'cdi', label: 'CDI' },
                  { value: 'cdd', label: 'CDD' },
                  { value: 'interim', label: 'Intérim' },
                  { value: 'independant', label: 'Indépendant' },
                  { value: 'autre', label: 'Autre' },
                ]} />
              <Field label="Employeur" value={profile.employeur} onChange={(v) => update('employeur', v)} />
              <Field label="Date d'embauche" type="date" value={profile.date_embauche} onChange={(v) => update('date_embauche', v)} />
              <Field label="Ancienneté (mois)" type="number" value={profile.anciennete_mois} onChange={(v) => update('anciennete_mois', v)} />
              <Field label="Expérience MdB (années)" type="number" value={profile.experience_mdb_annees} onChange={(v) => update('experience_mdb_annees', v)} />
              <Field label="Nb opérations réalisées" type="number" value={profile.nb_operations_realisees} onChange={(v) => update('nb_operations_realisees', v)} />
            </AccordionContent>
          </AccordionItem>

          {/* Revenus */}
          <AccordionItem value="revenus" className="border-zinc-800">
            <AccordionTrigger className="text-sm text-emerald-300 hover:no-underline">
              <span className="flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Revenus</span>
            </AccordionTrigger>
            <AccordionContent className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <Field label="Salaire net mensuel" type="number" value={profile.salaire_net_mensuel} onChange={(v) => update('salaire_net_mensuel', v)} suffix="€/mois" />
              <Field label="13ème mois (annuel)" type="number" value={profile.treizieme_mois} onChange={(v) => update('treizieme_mois', v)} suffix="€/an" />
              <Field label="Primes annuelles" type="number" value={profile.primes_annuelles} onChange={(v) => update('primes_annuelles', v)} suffix="€/an" />
              <Field label="Revenus emploi secondaire" type="number" value={profile.revenus_emploi_secondaire} onChange={(v) => update('revenus_emploi_secondaire', v)} suffix="€/mois" />
              <Field label="Revenus locatifs" type="number" value={profile.revenus_locatifs_mensuels} onChange={(v) => update('revenus_locatifs_mensuels', v)} suffix="€/mois" />
              <Field label="Dividendes" type="number" value={profile.revenus_dividendes_annuels} onChange={(v) => update('revenus_dividendes_annuels', v)} suffix="€/an" />
              <Field label="Pensions alimentaires reçues" type="number" value={profile.revenus_pension_alimentaire} onChange={(v) => update('revenus_pension_alimentaire', v)} suffix="€/mois" />
              <Field label="Allocations (CAF, ARE...)" type="number" value={profile.revenus_allocations} onChange={(v) => update('revenus_allocations', v)} suffix="€/mois" />
              <Field label="Autres revenus" type="number" value={profile.revenus_autres} onChange={(v) => update('revenus_autres', v)} suffix="€/mois" />
              <Field label="Précisez (autres)" value={profile.revenus_autres_detail} onChange={(v) => update('revenus_autres_detail', v)} placeholder="Ex: rente, donation..." />
            </AccordionContent>
          </AccordionItem>

          {/* Charges */}
          <AccordionItem value="charges" className="border-zinc-800">
            <AccordionTrigger className="text-sm text-orange-300 hover:no-underline">📉 Charges mensuelles</AccordionTrigger>
            <AccordionContent className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <Field label="Loyer (RP)" type="number" value={profile.loyer_mensuel} onChange={(v) => update('loyer_mensuel', v)} suffix="€/mois" />
              <Field label="Crédits immobiliers" type="number" value={profile.credits_immobiliers_mensualite} onChange={(v) => update('credits_immobiliers_mensualite', v)} suffix="€/mois" />
              <Field label="Crédits à la consommation" type="number" value={profile.credits_conso_mensualite} onChange={(v) => update('credits_conso_mensualite', v)} suffix="€/mois" />
              <Field label="Pensions alimentaires versées" type="number" value={profile.pensions_alimentaires_versees} onChange={(v) => update('pensions_alimentaires_versees', v)} suffix="€/mois" />
              <Field label="Autres charges" type="number" value={profile.autres_charges} onChange={(v) => update('autres_charges', v)} suffix="€/mois" />
              <Field label="Précisez (autres charges)" value={profile.autres_charges_detail} onChange={(v) => update('autres_charges_detail', v)} />
            </AccordionContent>
          </AccordionItem>

          {/* Patrimoine */}
          <AccordionItem value="patrimoine" className="border-zinc-800">
            <AccordionTrigger className="text-sm text-amber-300 hover:no-underline">🏦 Patrimoine</AccordionTrigger>
            <AccordionContent className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <Field label="Épargne disponible (livrets)" type="number" value={profile.epargne_disponible} onChange={(v) => update('epargne_disponible', v)} suffix="€" />
              <Field label="Épargne long terme (PEA, AV, PEL)" type="number" value={profile.epargne_long_terme} onChange={(v) => update('epargne_long_terme', v)} suffix="€" />
              <Field label="Valeur résidence principale" type="number" value={profile.valeur_residence_principale} onChange={(v) => update('valeur_residence_principale', v)} suffix="€" />
              <Field label="Autres biens immobiliers (valeur)" type="number" value={profile.autres_biens_immobiliers_valeur} onChange={(v) => update('autres_biens_immobiliers_valeur', v)} suffix="€" />
              <div className="md:col-span-2">
                <Field label="Précisez (autres biens)" value={profile.autres_biens_detail} onChange={(v) => update('autres_biens_detail', v)} placeholder="Ex: SCI familiale, terrain..." />
              </div>
              <Field label="Banque principale" value={profile.banque_principale} onChange={(v) => update('banque_principale', v)} />
              <Field label="Ancienneté relation bancaire (années)" type="number" value={profile.relation_banque_annees} onChange={(v) => update('relation_banque_annees', v)} />
              <Field label="Incidents bancaires (24 derniers mois)" type="number" value={profile.nb_incidents_bancaires_24m} onChange={(v) => update('nb_incidents_bancaires_24m', v)} />
            </AccordionContent>
          </AccordionItem>

          {/* Notes */}
          <AccordionItem value="notes" className="border-zinc-800">
            <AccordionTrigger className="text-sm text-zinc-300 hover:no-underline">📝 Notes complémentaires</AccordionTrigger>
            <AccordionContent className="pt-2">
              <Textarea
                value={profile.notes || ''}
                onChange={(e) => update('notes', e.target.value)}
                placeholder="Toute information utile à présenter au banquier..."
                className="bg-zinc-950 border-zinc-800 text-white text-sm min-h-32"
                rows={5}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};

export default BorrowerProfileCard;
